import { inject, Injectable } from '@angular/core';
import { from, map, Observable, switchMap } from 'rxjs';
import { ApiDraw, ApiDrawProfileCollection, ApiDrawProfileProjectionEmbeddedCollection, DrawApi } from './draw.api';
import {
  Draw,
  DrawRegistrationContextProjection,
  DrawTicketContextProjection,
  ProfileDrawProjection,
} from './draw.model';
import { GetCollectionResponse } from '@unicup-ticketing/utils';

@Injectable({
  providedIn: 'root',
})
export class DrawService {
  private readonly api = inject(DrawApi);

  public getDrawById(drawId: string): Observable<Draw> {
    return this.api.getDrawById<ApiDraw>(drawId).pipe(
      map(api => {
        return {
          id: api.id,
          displayName: api.displayName,
          state: api.state,
          maxGroupSize: 0,
        };
      }),
    );
  }

  public getDrawContextForRegistration(drawId: string): Observable<DrawRegistrationContextProjection> {
    return this.api.getDrawById<DrawRegistrationContextProjection>(drawId, 'regInfo');
  }

  public getDrawContextForTickets(drawId: string): Observable<DrawTicketContextProjection> {
    return this.api.getDrawById<DrawTicketContextProjection>(drawId, 'ticketInfo');
  }

  public retrieveUserProfileDraws(): Observable<ProfileDrawProjection> {
    return this.api
      .getDrawCollection<GetCollectionResponse<ApiDrawProfileProjectionEmbeddedCollection>>('profile')
      .pipe(
        map(response => response._embedded.draws),
        switchMap(response =>
          from(response).pipe(
            map(draw => {
              return {
                id: draw.id,
                displayName: draw.displayName,
                state: draw.state,
                registration: draw.registration,
              };
            }),
          ),
        ),
      );
  }

  public retrieveDraws(proj: string): Observable<Draw> {
    return this.api.getDrawCollection<GetCollectionResponse<ApiDrawProfileCollection>>(proj).pipe(
      map(response => response._embedded.draws),
      switchMap(response =>
        from(response).pipe(
          map(draw => {
            return {
              id: draw.id,
              displayName: draw.displayName,
              state: draw.state,
            };
          }),
        ),
      ),
    );
  }
}
